import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { sendEmail } from "./email.server";
import { logger } from "@/lib/logger.server";

const CONTACT_INBOX = process.env.CONTACT_INBOX || "contact@heallexa.com";

const escape = (s: string) =>
  s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));

export const submitContact = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      firstName: z.string().trim().min(1).max(80),
      lastName: z.string().trim().min(1).max(80),
      email: z.string().trim().email().max(200),
      phone: z.string().trim().min(7).max(40),
      topic: z.string().trim().max(80).default("General inquiry"),
      message: z.string().trim().min(10).max(2000),
      acceptTerms: z.literal(true),
      smsTransactional: z.boolean().default(false),
      smsMarketing: z.boolean().default(false),
      // ponytail: honeypot — bots fill hidden fields, humans don't. Real
      // spam volume would justify a rate limit / captcha; this covers the rest.
      website: z.string().max(200).optional(),
    })
  )
  .handler(async ({ data }) => {
    if (data.website) return { ok: true as const };

    const name = `${data.firstName} ${data.lastName}`;
    const rows: [string, string][] = [
      ["Name", name],
      ["Email", data.email],
      ["Phone", data.phone],
      ["Topic", data.topic],
      ["Terms accepted", "Yes"],
      ["SMS (service updates)", data.smsTransactional ? "Opted in" : "No"],
      ["SMS (marketing)", data.smsMarketing ? "Opted in" : "No"],
    ];

    await sendEmail({
      to: CONTACT_INBOX,
      reply_to: data.email,
      subject: `[Contact] ${data.topic} — ${name}`,
      html: `<div style="font-family:Inter,Arial,sans-serif;color:#0f172a;font-size:14px;line-height:1.6">
        <h2 style="font-size:18px;margin:0 0 16px">New contact form submission</h2>
        <table cellpadding="0" cellspacing="0" style="font-size:14px">
          ${rows
            .map(
              ([k, v]) =>
                `<tr><td style="padding:2px 16px 2px 0;color:#64748b">${k}</td><td>${escape(v)}</td></tr>`
            )
            .join("")}
        </table>
        <hr style="border:none;border-top:1px solid #e5e7eb;margin:20px 0" />
        <p style="white-space:pre-wrap;margin:0">${escape(data.message)}</p>
      </div>`,
      text: `${rows.map(([k, v]) => `${k}: ${v}`).join("\n")}\n\n${data.message}`,
    });

    logger.info("contact_form_submitted", { topic: data.topic });
    return { ok: true as const };
  });
